import { faCreditCard, faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { DatePicker } from "antd";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import CellBadge from "../../components/CellBadge";
import { getClientPayments } from "../../redux/Actions/paymentAction";
import DateRangeFilter from "../../statsComponents/DateRangeFilter";
import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-regular-svg-icons";
import { faApple, faGoogle } from "@fortawesome/free-brands-svg-icons";
import usePermissions from "../../hook/userPermission";
import moment from "moment";
const { RangePicker } = DatePicker;

const ClientReports = () => {
  const { hasPermission } = usePermissions();
  const [dateRange, setDateRange] = useState([null, null]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const { userInfo } = useSelector((state) => state.userLogin);
  const hotelId = userInfo.isAdmin === true ? userInfo?._id : userInfo?.hotel;
  const { loading, payments, totalPayments, currentPage } = useSelector(
    (state) => state.clientPayments
  );
  const {
    loading: loadingByDate,
    payments: paymentsByDate,
    totalPayments: totalPaymentsByDate,
  } = useSelector((state) => state.clientPayments);

  const dispatch = useDispatch();

  useEffect(() => {
    const [start, end] = dateRange || [null, null];
    dispatch(
      getClientPayments({
        clientId: hotelId,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        start: start ? start.toISOString() : null,
        end: end ? end.toISOString() : null,
      })
    );
  }, [pagination.pageIndex, pagination.pageSize, dateRange, dispatch, hotelId]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [null, null]);
    const [start, end] = dates || [null, null];

    if (start && end) {
      dispatch(
        getClientPayments(
          hotelId,
          pagination.pageIndex + 1,
          pagination.pageSize,
          start.toISOString(),
          end.toISOString()
        )
      );
    } else {
      dispatch(
        getClientPayments(
          hotelId,
          pagination.pageIndex + 1,
          pagination.pageSize
        )
      );
    }
  };

  const processedPayments = useMemo(
    () =>
      payments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [payments]
  );

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "rowNumber",
        size: 50,
        Cell: ({ row }) => (
          <span className="fw-semibold">{row.index + 1}.</span>
        ),
        enableGrouping: false,
      },
      {
        header: "Date",
        accessorKey: "createdAt",
        size: 120,
        Cell: ({ cell }) => moment(cell.getValue()).format("MM/DD/YYYY"),
      },
      {
        header: "Time",
        accessorKey: "time",
        size: 100,
        Cell: ({ cell }) =>
          new Date(cell.getValue()).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
      },
      {
        header: "Location",
        accessorKey: "clientLocation",
        size: 120,
        Cell: ({ cell }) => (
          <span className="text-muted fst-italic">{cell.getValue()}</span>
        ),
      },
      {
        header: "Transaction ID",
        accessorKey: "paymentIntentId",
        size: 250,
        Cell: ({ cell }) => (
          <span className="text-muted fst-italic">{cell.getValue()}</span>
        ),
      },
      {
        header: "Tip Type",
        accessorKey: "tipType",
        size: 100,
        Cell: ({ cell }) =>
          cell.getValue() === "teamTip" ? "Pool Tip" : "Direct Tip",
      },
      {
        header: "Service",
        accessorKey: "service",
        size: 120,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Cell: ({ cell }) =>
          cell.getValue() === "maid" ? (
            <CellBadge label={"Attendants"} />
          ) : cell.getValue() === "valet" ? (
            <CellBadge label={"Valet"} />
          ) : cell.getValue() === "bellman" ? (
            <CellBadge label={"Bell Staff"} />
          ) : cell.getValue() === "airbnb" ? (
            <CellBadge label={"Airbnb"} />
          ) : null,
      },
      {
        header: "Tip Amount",
        accessorKey: "tipAmount",
        size: 100,
        Cell: ({ cell }) => (
          <Box sx={{ color: "success.main" }}>${cell.getValue()}</Box>
        ),
      },
      {
        header: "Processing Fee",
        accessorKey: "processingFee",
        size: 100,
        Cell: ({ cell }) => (
          <Box sx={{ color: "success.main" }}>${cell.getValue()}</Box>
        ),
      },
      {
        header: "Total Amount",
        accessorKey: "amount",
        size: 100,
        Cell: ({ cell }) => (
          <Box sx={{ color: "success.main" }}>${cell.getValue()}</Box>
        ),
      },
      {
        header: "Customer paid fee",
        accessorKey: "feePaidByCustomer",
        size: 100,
        Cell: ({ cell }) => {
          const paid = cell.getValue();
          return (
            <Box display="flex" alignItems="center" className="text-muted">
              <FontAwesomeIcon
                icon={paid ? faCheckCircle : faTimesCircle}
                className="mx-1"
                color={paid ? "green" : "red"}
              />
              {paid ? "Yes" : "No"}
            </Box>
          );
        },
      },
      {
        header: "Payment Method",
        accessorKey: "paymentMethod",
        size: 100,
        Cell: ({ cell }) => (
          <Box>
            {cell.getValue() === "apple_pay" ? (
              <span className="text-muted">
                <FontAwesomeIcon icon={faApple} className="mx-1" />
                Pay
              </span>
            ) : cell.getValue() === "google_pay" ? (
              <span className="text-muted">
                <FontAwesomeIcon icon={faGoogle} className="mx-1" />
                Pay
              </span>
            ) : (
              <span className="text-muted">
                <FontAwesomeIcon icon={faCreditCard} className="mx-1" />
                Card
              </span>
            )}
          </Box>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        size: 100,
        Cell: ({ row }) => {
          const status = row.original.status?.toLowerCase();
          const badgeTypes = {
            refunded: "info",
            disputed: "danger",
            success: "success",
          };

          return (
            <Badge pill bg={badgeTypes[status] || "secondary"}>
              {status
                ? status.charAt(0).toUpperCase() + status.slice(1)
                : "Unknown"}
            </Badge>
          );
        },
      },
      {
        header: "Recipient",
        accessorKey: "maidName",
        size: 190,
        Cell: ({ cell }) => (
          <span className="text-muted">{cell.getValue() || "Not Defined"}</span>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: dateRange[0] && dateRange[1] ? paymentsByDate : processedPayments,
    state: {
      pagination,
      isLoading: dateRange[0] && dateRange[1] ? loadingByDate : loading,
    },
    onPaginationChange: setPagination,
    rowCount:
      dateRange[0] && dateRange[1] ? totalPaymentsByDate : totalPayments,
    manualPagination: true,
    enablePagination: true,
    enableColumnResizing: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableHiding: true,
    initialState: {
      density: "compact",
    },
    enableColumnFilters: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableColumnActions: false,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 700,
        overflowX: "auto",
        overflowY: "auto",
        marginBottom: 2,
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "12px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "18px",
        },
        "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        color: "#333",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box>
        <RangePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          className="mx-2"
          value={dateRange}
          onChange={handleDateRangeChange}
          placeholder={["Start Date", "End Date"]}
        />
        <div className="d-inline">
          <DateRangeFilter
            title="Quick Filters"
            setDateRange={handleDateRangeChange}
          />
        </div>
        {hasPermission("toDownload") && (
          <Button
            onClick={() => {
              const exportablePayments = table
                .getRowModel()
                .rows.map((row) => row.original);
              const csvRows = [
                columns.map((col) => col.header).join(","),
                ...exportablePayments.map((row) =>
                  columns
                    .map((col) => {
                      let value = row[col.accessorKey];

                      if (col.accessorKey === "tipType") {
                        value = value === "teamTip" ? "Pool Tip" : "Direct Tip";
                      }
                      if (col.accessorKey === "service") {
                        if (value === "maid") value = "Attendants";
                        else if (value === "valet") value = "Valet";
                        else if (value === "bellman") value = "Bell Staff";
                        else if (value === "airbnb") value = "Airbnb";
                      }
                      return JSON.stringify(value || "");
                    })
                    .join(",")
                ),
              ];
              const csvContent = csvRows.join("\n");
              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = "client-reports.csv";
              link.click();
            }}
            variant="outline-secondary"
            className="ms-3 border-0 fs-7"
          >
            <FontAwesomeIcon className="me-1" icon={faDownload} />
            Export CSV
          </Button>
        )}
      </Box>
    ),
  });

  return (
    <Card border="0" className="custom-shadow rounded-4 mt-4">
      <Card.Body>
        <MaterialReactTable table={table} />
      </Card.Body>
    </Card>
  );
};

export default ClientReports;
